import React from "react";
import { connect } from "react-redux";
import styled from "styled-components";

import { Button } from "@appcues/component-library";
import { updateAccountMeta } from "actions/account/meta";
import { trackEvent } from "actions/events";
import { showModal } from "actions/currentModal";
import * as satisfactionSelectors from "selectors/satisfaction";
import {
  getRecentResponseData,
  getResponsesBreakout
} from "helpers/satisfaction";
import * as userSelectors from "reducers/user";
import * as metaReducer from "reducers/account/meta";

import Loader from "components/Common/Loader";
import FeedbackTable from "components/SatisfactionSurveys/SatisfactionSurveysIndex/Dashboard/SatisfactionFeedback/FeedbackTable";
import { EXPORT_NPS_SURVEY_CSV_MODAL } from "constants/globals";
import { selectAccountSatisfactionSynced } from "reducers/account/satisfaction";

const SatisfactionFeedback = props => {
  const {
    satisfactionSynced,
    responses,
    className,
    propMapping,
    satisfactionId,
    trackEvent,
    showModal,
    onSave
  } = props;

  const { emailProp, userNameProp } = propMapping || {};

  if (!satisfactionSynced) {
    return (
      <div className={className}>
        <h2 className="feedback-table-loader-text">
          Retrieving recent survey submissions...
        </h2>
        <Loader />
      </div>
    );
  }

  const transformedResponses = getRecentResponseData(responses);
  const responseBreakout = getResponsesBreakout(transformedResponses);
  const responsesToday = responseBreakout.responsesToday;
  const responsesThisWeek = responseBreakout.responsesThisWeek;
  const olderResponses = responseBreakout.olderResponses;

  const numResponsesToday = responsesToday.length;
  const numResponsesThisWeek = responsesThisWeek.length;
  const numOlderResponses = olderResponses.length;

  const hasResponses = transformedResponses.length > 0;
  const exportButtonLabel = "Export CSV";

  const showExportModal = () => {
    trackEvent("Clicked Button", {
      buttonLabel: exportButtonLabel
    });

    showModal(EXPORT_NPS_SURVEY_CSV_MODAL, { stepId: satisfactionId });
  };

  return (
    <div className={className}>
      <div className="feedback-table-controls">
        <h3>
          {hasResponses ? "50 Most Recent Responses" : "No responses yet"}
        </h3>
        <Button onClick={showExportModal}>{exportButtonLabel}</Button>
      </div>
      <FeedbackTable
        name="Today"
        feedback={responsesToday}
        priority
        emailProp={emailProp}
        userNameProp={userNameProp}
        updateAccountMetaAndFlushMeta={onSave}
        className={numResponsesToday > 0 ? "" : "empty-table"}
        showSettings={numResponsesToday}
      />
      <hr className={numResponsesThisWeek > 0 ? "" : "empty-table"} />
      <FeedbackTable
        name="This Week"
        feedback={responsesThisWeek}
        priority
        emailProp={emailProp}
        userNameProp={userNameProp}
        updateAccountMetaAndFlushMeta={onSave}
        className={numResponsesThisWeek > 0 ? "" : "empty-table"}
        showSettings={!numResponsesToday && numResponsesThisWeek}
      />
      <hr
        className={`table-divider ${
          numOlderResponses > 0 ? "" : "empty-table"
        }`}
      />
      <FeedbackTable
        name="Older"
        feedback={olderResponses}
        emailProp={emailProp}
        userNameProp={userNameProp}
        updateAccountMetaAndFlushMeta={onSave}
        className={numOlderResponses > 0 ? "" : "empty-table"}
        showSettings={
          !numResponsesToday && !numResponsesThisWeek && numOlderResponses
        }
      />
    </div>
  );
};

const StyledSatisfactionFeedback = styled(SatisfactionFeedback)`
  width: 100%;
  padding: 20px 0 35px;

  .empty-table {
    display: none;
  }

  .feedback-table-loader-text {
    text-align: center;
    color: #ccc;
  }

  main.loader {
    height: 200px;
  }

  .feedback-table-controls {
    width: 100%;
    margin-bottom: 30px;

    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  h3 {
    margin-top: 0 !important;
    font-size: 24px !important;
    color: #4d4d4d;
  }
`;

function mapStateToProps(state) {
  if (selectAccountSatisfactionSynced(state)) {
    const accountMeta = metaReducer.selectAccountMeta(state);
    const user = userSelectors.selectUser(state);
    const propMapping = {
      emailProp: accountMeta.userEmailField,
      userNameProp: accountMeta.userNameField
    };

    const satisfactionId = satisfactionSelectors.selectAccountFirstSatisfactionId(
      state
    );

    return {
      responses: satisfactionSelectors.selectAccountSatisfactionRecentResponses(
        state
      ),
      satisfactionSynced: selectAccountSatisfactionSynced(state),
      accountId: `${accountMeta.id}`,
      accountUuid: accountMeta.uuid,
      currentUserEmail: user.email,
      satisfactionId: satisfactionId,
      propMapping
    };
  }
  return { satisfactionSynced: selectAccountSatisfactionSynced(state) };
}

const mapDispatchToProps = {
  onSave: updateAccountMeta,
  trackEvent,
  showModal
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StyledSatisfactionFeedback);
